import gsap from "gsap"
import { ScrollTrigger } from "gsap/scrolltrigger"

// gsap用のScrollTriggerプラグインを登録
gsap.registerPlugin(ScrollTrigger)

// UnicornCanvasクラスを定義
export default class UnicornCanvas {
    constructor(imageSrc, canvasId) {
        this.imageSrc = imageSrc
        this.canvas = document.getElementById(canvasId)
        this.ctx = this.canvas.getContext('2d')
        this.image = new Image()
        this.image.src = this.imageSrc
        this.particles = []
        this.mouseX = 99999
        this.mouseY = 99999
        this.repulseRange = 20
        this.repulseStrength = 1
        this.vx = 1
        this.vy = 1
        this.data = null
        this.init()
    }

    reset() {
        this.particles = []
        this.mouseX = 99999
        this.mouseY = 99999
    }

    // キャンバスとイベントリスナーを初期化
    init() {
        this.image.onload = () => {
            this.getCanvasSize()
            this.getImageData()
            this.setCanvas()

            window.addEventListener('resize', this.onResize())

            this.canvas.addEventListener('mousemove', (e) => {
                const rect = this.canvas.getBoundingClientRect()
                this.mouseX = e.clientX - rect.left
                this.mouseY = e.clientY - rect.top
            })

            this.inview()
        }


    }

    getCanvasSize() {
        this.canvas.width = parseInt(window.getComputedStyle(this.canvas.parentNode).width, 10)
        this.canvas.height = parseInt(window.getComputedStyle(this.canvas.parentNode).height, 10)
    }

    getImageData() {
        this.ctx.drawImage(this.image, 0, 0, this.canvas.width, this.canvas.height)
        this.data = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    }

    // キャンバスの寸法を設定し、画像を描画
    setCanvas() {
        this.particles = []
        this.initParticles(this.data)
        this.render(this.ctx)
    }


    onResize() {
        this.setCanvas()
    }

    // ScrollTriggerの設定を構成
    inview() {
        ScrollTrigger.create({
            trigger: this.canvas,
            start: "top center",
            end: "bottom center",
            onEnter: () => this.startAnimation(),
            onLeave: () => this.stopAnimation(),
            onEnterBack: () => this.startAnimation(),
            onLeaveBack: () => this.stopAnimation()
        })
    }

    startAnimation() {
        this.animationFrameId = requestAnimationFrame(this.animate.bind(this))
    }

    stopAnimation() {
        cancelAnimationFrame(this.animationFrameId)
    }

    animate() {
        this.render()
        this.animationFrameId = requestAnimationFrame(this.animate.bind(this))
    }

    // 画像データに基づいてパーティクルを初期化
    initParticles(data) {
        for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] == 0) continue
            if (data[i + 3] > 100) {
                const x = (i / 4) % this.canvas.width
                const y = Math.floor((i / 4) / this.canvas.width)
                const radius = data[i + 3] / 255
                const dx = Math.random() * this.vx
                const dy = Math.random() * this.vy
                // const startX = dx * this.canvas.width
                // const startY = dy * this.canvas.height
                const startX = this.canvas.width / 2 + (Math.random() * this.canvas.height / 4 - this.canvas.height / 8)
                const startY = this.canvas.height / 2 + (Math.random() * this.canvas.height / 4 - this.canvas.height / 8)
                const particle = new Particle(this.ctx, startX, startY, radius, dx, dy, i)
                particle.originalX = x
                particle.originalY = y
                particle.opacity = Math.random() // Initialize opacity randomly
                this.particles.push(particle)
            }
        }
    }

    // アニメーションフレームをレンダリングし、パーティクルの位置を更新
    render() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.particles.forEach(particle => {
            let dist = Math.hypot(particle.x - this.mouseX, particle.y - this.mouseY)
            if (dist < this.repulseRange) {
                let angle = Math.atan2(particle.y - this.mouseY, particle.x - this.mouseX)
                particle.x += Math.cos(angle) * this.repulseStrength * Math.random() + Math.random() * 2 - 1
                particle.y += Math.sin(angle) * this.repulseStrength * Math.random() + Math.random() * 2 - 1
                particle.targetSize = (particle.size * this.repulseRange + Math.random()) * 3 - 1
                particle.targetOpacity = 0.1 // Target a lower opacity when close to mouse
            } else {
                particle.targetSize = particle.size
                particle.targetOpacity = 1 // Reset opacity when far from mouse
            }
            particle.update()
        })
    }
}

// UnicornCanvasで使用されるParticleクラスを定義
class Particle {
    constructor(ctx, x, y, size, dx, dy, index) {
        this.ctx = ctx
        this.x = x
        this.y = y
        this.size = size
        this.dx = dx
        this.dy = dy
        this.index = index
        this.targetSize = size
        this.currentSize = size * Math.random()
        this.originalX = x
        this.originalY = y
        this.opacity = 0 // Default opacity
        this.targetOpacity = 1 // Target opacity
    }

    // キャンバス上にパーティクルをレンダリング
    render() {
        this.ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`
        this.ctx.beginPath()
        this.ctx.arc(this.x, this.y, this.currentSize, 0, Math.PI * 2, true)
        this.ctx.fill()
    }

    // アニメーションのためのパーティクルプロパティを更新
    update() {
        const delay = 0.03
        const easeOut = (t) => t * (2 - t)
        const lerp = (a, b, n) => (1 - n) * a + n * b
        if (this.currentSize !== this.targetSize) {
            this.currentSize = lerp(this.currentSize, this.targetSize, easeOut(delay))
        }
        if (this.opacity !== this.targetOpacity) {
            this.opacity = lerp(this.opacity, this.targetOpacity, easeOut(delay))
        }
        if (this.x !== this.originalX || this.y !== this.originalY) {
            this.x = lerp(this.x, this.originalX, easeOut(delay))
            this.y = lerp(this.y, this.originalY, easeOut(delay))
        }
        this.render()
    }
}
